// File: src/App.js

import React, { useState, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
} from 'react-router-dom';

/********************************************************
 * 1) IMPORT THE GLOBAL AXIOS Global
 ********************************************************/
import '../src/utils/axiosGlobal';
import MixerIntroWizardPage from './pages/MixerIntroWizardPage';
// Import AuthProvider
import { AuthProvider } from './context/AuthContext';
import OrientationPrompt from './components/OrientationPrompt';
import { initAnalytics } from "./utils/analytics";

// IMPORTANT: Import ToasterProvider and ToasterBar
import { ToasterProvider, useToaster } from './context/ToasterContext';
import ToasterBar from './components/ToasterBar';

// Import MatchFlowProvider (scoped below, not global)
import { MatchFlowProvider } from './components/MatchFlow/MatchFlowContext';

// ---> Wrap the entire UI in NotificationsProvider
import { NotificationsProvider } from './context/NotificationsProvider';
import DeviceRouteGuard from './routes/DeviceRouteGuard';
import ColorMixerPortal from './pages/mixer/ColorMixerPortal';
// Import pages
import Login from './pages/Login';
import Register from './pages/registration/Register';
import MemberApplication from './pages/registration/MemberApplication';
import Events from './pages/Events';
import AdminEvents from './pages/AdminEvents';
import Settings from './pages/Settings';
import Friends from './pages/Friends';
import ForgotPassword from './pages/ForgotPassword';
import LegalPage from './pages/LegalPage';
import ModerationPanel from './pages/ModerationPanel';
import DesktopLanding from './pages/DesktopLanding';
import NotificationsPage from './pages/NotificationsPage';
import ProfilePage from './pages/ProfilePage';
import ProfileEditPage from './pages/ProfileEditPage';
import TourPage from './pages/TourPage';
import ChatsPage from './components/friends/ChatPage';
import ContactPage from './components/friends/ContactPage';
import MatchesAndRequestsPage from './components/friends/MatchesAndRequestsPage';
import IntroWizardPage from './pages/IntroWizardPage';
import MatchFlowMobile from './pages/MatchFlowMobile';
import FoundMatchPage from './components/MatchFlow/FoundMatchPage';
import NavigateSpotPage from './components/MatchFlow/NavigateSpotPage';
import CodeVerificationPage from './components/MatchFlow/CodeVerificationPage';
import InMatchPage from './components/MatchFlow/InMatchPage';
import PostMatchPage from './components/MatchFlow/PostMatchPage';
import UpdateAccountPage from './pages/UpdateAccountPage';
import DeleteAccountPage from './pages/DeleteAccountPage';
import SupportFeedback from './pages/SupportFeedback';
import EventPreview from './pages/EventPreview';
import EventQuizPage from './pages/EventQuizPage';
import Bouncer from './pages/Bouncer';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import RevealQuiz from "./pages/reveal/RevealQuiz";
import RevealPortal from "./pages/reveal/RevealPortal";
import RevealPartner from "./pages/reveal/RevealPartner";
import RevealIntroWizardPage from './pages/reveal/RevealIntroWizardPage';
import RevealIceBreakers from './pages/reveal/RevealIcebreakers';
import RevealCodeVerification from "./pages/reveal/RevealCodeVerification";
import RevealInMatch from "./pages/reveal/RevealInMatch";
import NeutralIntroWizardPage from './pages/neutral/NeutralIntroWizardPage';
import NeutralWhereToMeetPage from './pages/neutral/NeutralWhereToMeetPage';
import NeutralPortal from './pages/neutral/NeutralPortal';
import HostDashboard from './pages/host/HostDashboard';
import HostAddEventPage from './pages/host/HostAddEventPage';
import HostEventHistoryPage from './pages/host/HostEventHistoryPage';
import HostPayouts from './pages/host/HostPayouts';
import HostLogin from './pages/host/HostLogin';
import HostDesktopPortal from './pages/host/HostDesktopPortal';
import HostRecruitmentSurvey from './pages/recruiting/HostRecruitmentSurvey';
import RecruitmentSurveySubmitted from './pages/recruiting/RecruitmentSurveySubmitted';
import HostTraining from './pages/HostTraining';
import HostDocsGatePage from './pages/host/HostDocsGatePage';
import PostEventFeedbackSheet from './components/PostEventFeedbackSheet';
import SubscriptionPage from "./pages/SubscriptionPage";
import TicketsPage from './pages/TicketsPage';
import NeutralGuestCheckInPage from './pages/neutral/NeutralGuestCheckInPage';
import NeutralAgendaPage from './pages/neutral/NeutralAgendaPage';
import ReferralPartnerApplication from './pages/ReferralPartnerApplication';
import ReferralPartnerDashboardPage from './pages/ReferralPartnerDashboardPage';
import ReferralPartnerTraining from './pages/ReferralPartnerTraining';
import PortalPage from './pages/PortalPage';

// Layouts
import NoNavLayout from './layouts/NoNavLayout';
import StandardLayout from './layouts/StandardLayout';
import TicketPurchasePage from './pages/TicketPurchasePage';

// Routes
import ConditionalEventsLayout from './routes/ConditionalEventsLayout';

// ProtectedRoute
import ProtectedRoute from './components/ProtectedRoute';

// Radar import FIRST, then initialize
import Radar from 'radar-sdk-js';

import LoadingScreen from './components/LoadingScreen';

import isPWA from './utils/isPWA';

// ➕ Route guard to kill flicker on Reveal pages
import RevealRouteGuard from './routes/RevealRouteGuard';

Radar.initialize('prj_live_pk_0efe00ed0f60f7652ba9a09d19942366fc81205a');

/**
 * Toggle this flag to ENABLE / DISABLE the device guard globally.
 *   - true  = device guard ACTIVE
 *   - false = device guard DISABLED
 */
// const DEVICE_GUARD_ENABLED = true; // unused toggle
/**
 * Checks user-agent to see if we're on a "true" mobile device / tablet
 * (rather than just a small desktop browser window).
 */
//function isTrueMobileDevice() {
//  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  //return /android|avantgo|blackberry|iphone|ipad|ipod|kindle|palm|psp|series60|symbian|windows ce|windows phone|xda|mobile/i.test(
    //userAgent.toLowerCase()
 // );
// }

/** Scope the MatchFlow provider to only the MatchFlow routes */
function MatchFlowScope() {
  return (
    <MatchFlowProvider>
      <Outlet />
    </MatchFlowProvider>
  );
}

function AppContent() {
  const [loading, setLoading] = useState(isPWA());
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);
  const [feedbackEvent, setFeedbackEvent] = useState(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const { addToast } = useToaster();

    useEffect(() => {
    initAnalytics();
  }, []);
  
  useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => setLoading(false), 1500);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  useEffect(() => {
    let active = true;

    async function checkPendingFeedback() {
      try {
        const token = localStorage.getItem('accessToken');
        if (!token) return;

        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/feedback/pending-event`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await res.json();

        if (!active) return;

        if (data?.event?.eventId) {
          setFeedbackEvent(data.event);
          setShowFeedbackPopup(true);
        }
      } catch (err) {
        console.error('feedback check failed:', err);
      }
    }

    if (!loading) {
      checkPendingFeedback();
    }

    return () => {
      active = false;
    };
  }, [loading]);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <>
      <ToasterBar />
      <OrientationPrompt />

      <Routes>
        {/* Device guard wraps all routes */}
        <Route element={<DeviceRouteGuard />}>
<Route path="/" element={<ConditionalEventsLayout />} />
          <Route path="/events/:eventId" element={<EventPreview />} />
          <Route path="/events/:eventId/purchase" element={<TicketPurchasePage />} />
<Route
  path="/membership/purchase"
  element={
    <ProtectedRoute>
      <TicketPurchasePage />
    </ProtectedRoute>
  }
/>
          <Route
            path="/mixer/events/:eventId"
            element={
              <ProtectedRoute>
                <ColorMixerPortal />
              </ProtectedRoute>
            }
          />

          <Route
            path="/mixer/events/:eventId/quiz"
            element={
              <ProtectedRoute>
                <EventQuizPage />
              </ProtectedRoute>
            }
          />

          <Route path="/desktoplanding" element={<DesktopLanding />} />
          <Route path="/desktoplanding" element={<DesktopLanding />} />

<Route path="/referral-partner/training" element={<ReferralPartnerTraining />} />


          <Route path="/admin/events" element={<AdminEvents />} />
          <Route path="/admin/moderation" element={<ModerationPanel />} />

          <Route path="/reveal/:eventId/intro" element={<RevealIntroWizardPage />} />

          <Route
            path="/reveal/:eventId/quiz"
            element={
              <ProtectedRoute>
                <RevealQuiz />
              </ProtectedRoute>
            }
          />

          <Route
            path="/reveal/:eventId/portal"
            element={
              <ProtectedRoute>
                <RevealRouteGuard pageKind="portal" />
              </ProtectedRoute>
            }
          >
            <Route index element={<RevealPortal />} />
          </Route>

          <Route
            path="/reveal/:eventId/partner"
            element={
              <ProtectedRoute>
                <RevealRouteGuard pageKind="partner" />
              </ProtectedRoute>
            }
          >
            <Route index element={<RevealPartner />} />
          </Route>

          <Route
            path="/reveal/:eventId/verify"
            element={
              <ProtectedRoute>
                <RevealRouteGuard pageKind="partner" />
              </ProtectedRoute>
            }
          >
            <Route index element={<RevealCodeVerification />} />
          </Route>

          <Route
            path="/reveal/:eventId/inmatch"
            element={
              <ProtectedRoute>
                <RevealRouteGuard pageKind="inmatch" />
              </ProtectedRoute>
            }
          >
            <Route index element={<RevealInMatch />} />
          </Route>

          <Route
            path="/reveal/:eventId/icebreakers"
            element={
              <ProtectedRoute>
                <RevealIceBreakers />
              </ProtectedRoute>
            }
          />

          <Route
            path="/neutral/:eventId/intro"
            element={
              <ProtectedRoute>
                <NeutralIntroWizardPage />
              </ProtectedRoute>
            }
          />


          <Route
          path="/neutral/:eventId/where-to-meet" 
element={
 <ProtectedRoute>
<NeutralWhereToMeetPage />
  </ProtectedRoute>
            }
          />


          <Route
            path="/neutral/:eventId/portal"
            element={
              <ProtectedRoute>
                <NeutralPortal />
              </ProtectedRoute>
            }
          />

          <Route
  path="/neutral/:eventId/check-in"
  element={
    <ProtectedRoute>
      <NeutralGuestCheckInPage />
    </ProtectedRoute>
  }
/>

<Route
  path="/neutral/:eventId/agenda"
  element={<NeutralAgendaPage />}
/>

          <Route
            path="/*"
            element={
              <div style={{ marginTop: 0 }}>
                <Routes>
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />

                  <Route element={<NoNavLayout />}>
                    <Route path="/login" element={<Login />} />
                    <Route path="/host/login" element={<HostLogin />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/apply" element={<MemberApplication />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/tour" element={<TourPage />} />
                    <Route path="/bouncer" element={<Bouncer />} />
                    <Route path="/legal" element={<LegalPage />} />
                    <Route path="/legal/:section" element={<LegalPage />} />
                    <Route path="/referral-partner/apply" element={<ReferralPartnerApplication />}/>
<Route path="/host-recruiting/survey" element={<HostRecruitmentSurvey />} />
<Route path="/host-recruiting/survey/submitted" element={<RecruitmentSurveySubmitted />} />
<Route path="/host-recruiting/training" element={<HostTraining />} />
                    <Route path="/membership" element={<SubscriptionPage />} />
<Route
  path="/partner"
  element={
    <ProtectedRoute>
      <ReferralPartnerDashboardPage />
    </ProtectedRoute>
  }
/>     

<Route
  path="/portal"
  element={
    <ProtectedRoute>
      <PortalPage />
    </ProtectedRoute>
  }
/>

               <Route
                      path="/host/docs"
                      element={
                        <ProtectedRoute>
                          <HostDocsGatePage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/host/add"
                      element={
                        <ProtectedRoute>
                          <HostAddEventPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/host/add/:eventId"
                      element={
                        <ProtectedRoute>
                          <HostAddEventPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/host/history"
                      element={
                        <ProtectedRoute>
                          <HostEventHistoryPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/host/portal"
                      element={
                        <ProtectedRoute>
                          <HostDesktopPortal />
                        </ProtectedRoute>
                      }
                    />

                    <Route path="/host/payouts" element={<HostPayouts />} />

                    <Route
                      path="/intro"
                      element={
                        <ProtectedRoute>
                          <IntroWizardPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/intro-mixer"
                      element={
                        <ProtectedRoute>
                          <MixerIntroWizardPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route element={<MatchFlowScope />}>
                      <Route
                        path="/matchflow"
                        element={
                          <ProtectedRoute>
                            <MatchFlowMobile />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/match-found"
                        element={
                          <ProtectedRoute>
                            <FoundMatchPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/NavigateSpotPage"
                        element={
                          <ProtectedRoute>
                            <NavigateSpotPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/code-verification"
                        element={
                          <ProtectedRoute>
                            <CodeVerificationPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/inmatchpage"
                        element={
                          <ProtectedRoute>
                            <InMatchPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/post-match"
                        element={
                          <ProtectedRoute>
                            <PostMatchPage />
                          </ProtectedRoute>
                        }
                      />
                    </Route>

                    <Route
                      path="/account-update/:mode"
                      element={
                        <ProtectedRoute>
                          <UpdateAccountPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/delete-account/:userId"
                      element={
                        <ProtectedRoute>
                          <DeleteAccountPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/support-feedback"
                      element={
                        <ProtectedRoute>
                          <SupportFeedback />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/profile/edit"
                      element={
                        <ProtectedRoute>
                          <ProfileEditPage />
                        </ProtectedRoute>
                      }
                    />
                  </Route>

                  <Route path="/events" element={<ConditionalEventsLayout />} />

                  <Route
                    element={
                      <ProtectedRoute>
                        <StandardLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/friends" element={<Friends />} />
                    <Route path="/tickets" element={<TicketsPage />} />
                    <Route path="/notifications" element={<NotificationsPage />} />
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="/friends/chats" element={<ChatsPage />} />
                    <Route path="/friends/contacts" element={<ContactPage />} />
                    <Route path="/friends/requests" element={<MatchesAndRequestsPage />} />
                    <Route path="/host" element={<HostDashboard />} />
                  </Route>

                  <Route
                    path="/friends/chats/:chatId"
                    element={
                      <ProtectedRoute>
                        <StandardLayout hideNav />
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              </div>
            }
          />
        </Route>
      </Routes>

   {window.innerWidth <= 900 && showFeedbackPopup && (
  <PostEventFeedbackSheet
    isOpen={showFeedbackPopup}
    eventId={feedbackEvent?.eventId || ""}
    eventName={feedbackEvent?.eventName || ""}
    userId={
      feedbackEvent?.userId ||
      localStorage.getItem("userId") ||
      null
    }
    attendanceStatus={
      feedbackEvent?.attendanceStatus ||
      "attended"
    }
    hasEnded={true}
    attendees={
      Array.isArray(feedbackEvent?.attendees)
        ? feedbackEvent.attendees
        : []
    }
    isSubmitting={feedbackSubmitting}
    onClose={async () => {
      if (feedbackSubmitting) {
        return;
      }

      try {
        if (feedbackEvent?.eventId) {
          setFeedbackSubmitting(true);

          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/feedback/event-experience/dismiss`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem(
                  "accessToken"
                )}`,
              },
              body: JSON.stringify({
                eventId: feedbackEvent.eventId,
              }),
            }
          );

          if (!response.ok) {
            const errorData = await response
              .json()
              .catch(() => ({}));

            throw new Error(
              errorData?.message ||
                "Could not dismiss feedback."
            );
          }
        }
      } catch (err) {
        console.error("dismiss failed:", err);
      } finally {
        setShowFeedbackPopup(false);
        setFeedbackEvent(null);
        setFeedbackSubmitting(false);
      }
    }}
    onSubmit={async (payload) => {
      if (!feedbackEvent?.eventId) {
        throw new Error(
          "Missing feedback event ID."
        );
      }

      try {
        setFeedbackSubmitting(true);

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/feedback/event-experience`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem(
                "accessToken"
              )}`,
            },
            body: JSON.stringify({
              eventId: feedbackEvent.eventId,
              ...payload,
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response
            .json()
            .catch(() => ({}));

          throw new Error(
            errorData?.message ||
              "Could not submit feedback."
          );
        }
      } catch (err) {
        console.error("submit failed:", err);
        throw err;
      } finally {
        setFeedbackSubmitting(false);
      }
    }}
    onOpenChats={() => {
      setShowFeedbackPopup(false);
      setFeedbackEvent(null);
      setFeedbackSubmitting(false);

      window.location.assign(
        "/friends"
      );
    }}
  />
)}
    </>
  );
}
function App() {
  return (
    <AuthProvider>
      <Router>
        <ToasterProvider>
          <NotificationsProvider>
            <AppContent />
          </NotificationsProvider>
        </ToasterProvider>
      </Router>
    </AuthProvider>
  );
}

export default App;
 